import React, { forwardRef, useId, useState } from 'react';
import Icon from './Icon';

/**
 * <Input
 *   label="Email"
 *   type="email"
 *   value={...}
 *   onChange={...}
 *   error="Required"
 *   leftIcon="mail"
 * />
 *
 * For passwords, pass variant="password" to get a show/hide toggle.
 */
const Input = forwardRef(function Input({
  label,
  hint,
  error,
  leftIcon,
  rightIcon,
  variant,
  type = 'text',
  id: idProp,
  className = '',
  style,
  labelStyle,
  containerStyle,
  ...rest
}, ref) {
  const uid    = useId();
  const id     = idProp || uid;
  const errId  = `${id}-error`;
  const hintId = `${id}-hint`;

  const [reveal, setReveal] = useState(false);
  const isPassword = variant === 'password' || type === 'password';
  const actualType = isPassword ? (reveal ? 'text' : 'password') : type;

  return (
    <div className={`form-group ${className}`} style={containerStyle}>
      {label && (
        <label htmlFor={id} className="form-label" style={labelStyle}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        {leftIcon && (
          <span
            aria-hidden
            style={{
              position: 'absolute', left: 10, top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              display: 'flex', alignItems: 'center',
              pointerEvents: 'none',
            }}
          >
            {typeof leftIcon === 'string' ? <Icon name={leftIcon} size="sm" /> : leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={id}
          type={actualType}
          className={`form-control${error ? ' is-invalid' : ''}`}
          aria-invalid={!!error || undefined}
          aria-describedby={[error && errId, hint && hintId].filter(Boolean).join(' ') || undefined}
          style={{
            ...(leftIcon ? { paddingLeft: 34 } : null),
            ...((rightIcon || isPassword) ? { paddingRight: 40 } : null),
            ...style,
          }}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setReveal(r => !r)}
            aria-label={reveal ? 'Hide password' : 'Show password'}
            aria-pressed={reveal}
            style={{
              position: 'absolute', right: 10, top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent', border: 'none',
              color: 'var(--text-muted)', cursor: 'pointer',
              padding: 4, display: 'flex', alignItems: 'center',
              borderRadius: 'var(--radius-sm)',
              transition: 'color var(--dur-fast)',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
          >
            <Icon name={reveal ? 'viewOff' : 'view'} size="sm" />
          </button>
        )}
        {!isPassword && rightIcon && (
          <span
            aria-hidden
            style={{
              position: 'absolute', right: 10, top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              display: 'flex', alignItems: 'center',
              pointerEvents: 'none',
            }}
          >
            {typeof rightIcon === 'string' ? <Icon name={rightIcon} size="sm" /> : rightIcon}
          </span>
        )}
      </div>
      {error && (
        <div id={errId} role="alert" style={{
          display: 'flex', alignItems: 'center', gap: 5,
          fontSize: 'var(--fs-xs)', color: 'var(--danger)', marginTop: 4,
        }}>
          <Icon name="warning" size="xs" color="var(--danger)" />
          {error}
        </div>
      )}
      {!error && hint && (
        <div id={hintId} style={{
          fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginTop: 4,
        }}>
          {hint}
        </div>
      )}
    </div>
  );
});

export default Input;
